import { native } from '@forest/native-services'
import { useStore } from '@nanostores/react'
import { Dialog } from 'radix-ui'
import { useEffect, useState } from 'react'
import { preload } from 'react-dom'
import imgPlusProduct from '~/assets/images/product-plus.png'
import { useEntitlementQuery } from '~/hooks/use-entitlement-query'
import { useTrialEligibility } from '~/hooks/use-trial-eligibility'
import { m } from '~/paraglide/messages'
import { $config } from '~/store/config'
import { getBenefitItems } from './benefit-list'
import { SubscribeFlowBg } from './graphics/subscribe-flow-bg'
import { BackButtonIcon } from './icons/back-button'
import { SubscribeDialog } from './subscribe-dialog'

export function PassPaywallDialog(props: {
  isOpen: boolean
  onClose: () => void
}) {
  const [showSubscribeDialog, setShowSubscribeDialog] = useState(false)
  const config = useStore($config)
  const { data: entitlement } = useEntitlementQuery()
  const isPremium = entitlement?.isPremium ?? false

  useEffect(() => {
    if (!props.isOpen) return
    void native.general.logEvent({
      event_name: 'page_view',
      event_params: { page_name: 'game_pass_paywall' },
    })
  }, [props.isOpen])

  if (!config) return null

  preload(config.iap_plus_sku_img, { as: 'image' })

  return (
    <Dialog.Root
      open={props.isOpen}
      onOpenChange={(open) => {
        if (!open) {
          props.onClose()
          setShowSubscribeDialog(false)
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Content
          className="fixed inset-0 z-20 flex flex-col overflow-y-hidden bg-[#001721] focus-visible:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          aria-describedby=""
        >
          <div className="pointer-events-none absolute left-0 right-0 top-0 -z-10 h-[900px] overflow-x-hidden bg-top">
            <div className="absolute left-1/2 top-0 h-full -translate-x-1/2">
              <SubscribeFlowBg />
            </div>
          </div>

          <Dialog.Title className="sr-only">{m.btn_unlock_plus()}</Dialog.Title>

          {/* Nav bar */}
          <div className="absolute left-0 right-0 top-0 z-10 mx-auto flex w-full max-w-[640px] justify-between px-4 pt-[calc(16px+var(--safe-area-top))]">
            <Dialog.DialogClose className="size-[42px] rounded-[50%] bg-white/80 backdrop-blur">
              <BackButtonIcon />
            </Dialog.DialogClose>
            <div></div>
          </div>

          {/* Scrollable content */}
          <div className="h-full overflow-y-auto px-8 pb-40 pt-[calc(92px+var(--safe-area-top))]">
            <div className="mx-auto flex max-w-[640px] flex-col items-center gap-6">
              <h2 className="whitespace-pre-line px-4 text-center text-2xl font-bold text-white">
                {m.focus_pass_iap_page_title()}
              </h2>
              <img
                src={config.iap_plus_sku_img || imgPlusProduct}
                className="h-[188px] w-[282px] object-contain"
                alt=""
              />

              <BenefitList isPremium={isPremium} />
            </div>
          </div>

          {/* Fixed bottom CTA */}
          <div
            className="fixed bottom-0 left-0 right-0 bg-[#001721] px-6 pb-12 pt-3"
            style={{
              boxShadow: '0 -3px 3px 0 rgba(255, 255, 255, 0.20)',
            }}
          >
            <div className="mx-auto max-w-[640px]">
              <SubscribeButton onClick={() => setShowSubscribeDialog(true)} />
            </div>
          </div>

          <SubscribeDialog
            isOpen={showSubscribeDialog}
            isPremium={isPremium}
            onClose={() => {
              setShowSubscribeDialog(false)
              props.onClose()
            }}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function BenefitList(props: { isPremium: boolean }) {
  return (
    <div className="gradient-outline w-full rounded-[20px] bg-[#00A1E8]/10 p-6 [--og:linear-gradient(180deg,rgba(29,255,255,0.1),rgba(29,255,255,0))] [--ow:3px]">
      <ul className="flex flex-col gap-4">
        {getBenefitItems(props.isPremium).map((item, index) => (
          <li key={index} className="flex items-center gap-3">
            <img src={item.icon} className="size-10 shrink-0" alt="" />
            <span className="text-sm font-semibold text-white">
              {item.label()}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function SubscribeButton(props: { onClick: () => void }) {
  const { isTrial } = useTrialEligibility()

  return (
    <button
      className="group mx-auto h-[52px] w-full max-w-[640px] rounded-xl p-px [background-image:linear-gradient(180deg,#C5FF70_0%,#69FF78_45%,#30D3FF_100%)]"
      onClick={props.onClick}
    >
      <span className="flex h-full w-full items-center justify-center rounded-[11px] font-bold text-[#176361] [background-image:linear-gradient(106deg,#C5FF70_13%,#30D3FF_97%)] group-active:[background-image:linear-gradient(90deg,#94D66E_0%,#2BB8D3_100%)]">
        {isTrial ? m.btn_try_plus_free() : m.btn_unlock_plus()}
      </span>
    </button>
  )
}
